import { useContext } from 'react';

import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import {
  actionAuthor,
  ActionPerson,
  type ActionData,
} from '@/karehero-app/actions/ActionDetail';

export type Item = ActionData & {
  /**
   * Past its due date. Was `section: 'overdue' | 'upcoming'`, which grouped the
   * list under two headings — but 'upcoming' only ever meant 'not overdue', and
   * two headings over a handful of actions was a table of contents for a page
   * you can already see. The list is one list now and the row says it itself.
   */
  overdue?: boolean;
  /**
   * When it is due, as an ISO date, for the week the list is showing.
   *
   * Separate from `schedule`, which is the line the row prints and is not
   * always a date — a repeat says "Every Tuesday" and an intake action says
   * "You said you'd do this later". A string nobody can put in a week is fine
   * to show and no use to sort by, so the date is its own field.
   */
  date?: string;
  schedule?: string;
  who: string;
};

/**
 * The date line. Overdue swaps the word rather than adding one, and only where
 * the string is a due date: a repeat ("Every Tuesday") or a note ("You said
 * you'd do this later") is not something that can be late.
 */
const scheduleLine = (action: Item) =>
  action.overdue && action.schedule?.startsWith('Due ')
    ? `Overdue ${action.schedule.slice('Due '.length)}`
    : action.schedule;

// Wrapper component: a single action row. Composes Card + Checkbox + Typography
// + Avatar. Consume it with an action and the toggle/open callbacks.
const ActionCard = ({
  action,
  onToggle,
  onOpen,
}: {
  action: Item;
  onToggle: (id: number) => void;
  onOpen: (action: Item) => void;
}) => {
  const person = useContext(ActionPerson);
  const regarding = person ? person(action) : action.regarding;
  return (
    <Card variant='listItem' sx={{ p: 2 }} onClick={() => onOpen(action)}>
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.5}>
        <Checkbox
          checked={action.done}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggle(action.id)}
          color='primary'
          sx={{ p: 0, mt: '2px' }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          <Typography
            variant='h6'
            sx={{ textDecoration: action.done ? 'line-through' : 'none' }}
          >
            {action.title}
          </Typography>
          {action.schedule && (
            // Red where it has passed, and in sx: the variant's baked colour
            // beats Typography's color prop.
            <Typography
              variant='body1'
              sx={{
                color:
                  action.overdue && !action.done
                    ? 'error.main'
                    : 'text.primary',
              }}
            >
              {scheduleLine(action)}
            </Typography>
          )}
          {/* Only when there is somebody to name. An action the app set itself
            has no author, and "From KareHero" is noise on a list of things the
            app just told you about. */}
          {!person && actionAuthor(action) && (
            // Secondary in sx, not the color prop, which llama's baked variant
            // colour overrides. The date is the line that matters on a row you
            // are scanning; who set it is context.
            <Typography variant='body1' sx={{ color: 'text.secondary' }}>
              From {actionAuthor(action)}
            </Typography>
          )}
          {/* Who it is about, where a list mixes people: Home, for everyone you
            care for. The same chip as a claim's progress. */}
          {regarding && (
            <Box sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
              <Chip size='small' label={regarding} />
            </Box>
          )}
        </Stack>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'custom.neutral.400',
            color: 'custom.neutral.50',
            fontSize: 11,
          }}
        >
          {action.who}
        </Avatar>
      </Stack>
    </Card>
  );
};

export default ActionCard;
